import { Col, Input, Row } from 'antd';
import React, { FC, MouseEventHandler, useState } from 'react';
import FieldLabel from 'shared/components/FieldLabel';
import { InputFieldProps } from 'shared/components/InputField';
import {
  useFormContext,
  Controller,
  ControllerRenderProps,
  FieldValues,
} from 'react-hook-form';
import Error from '../../Error';
import clsx from 'clsx';
import styles from './rhfInputField.module.scss';
import { InfoCircleOutlined } from '@ant-design/icons';

const RHFInputField: FC<InputFieldProps> = props => {
  const {
    name,
    placeholderstring,
    label,
    isShowLabel,
    disabled,
    onClick,
    onFocus,
    isNullable,
    onChange,
    suffix,
    info,
    infoClick,
    readOnly,
    textarea,
    ...remainingProps
  } = props;
  const { TextArea } = Input;
  const [showLabel, setshowLabel] = useState<boolean>(
    !!disabled ?? !!isShowLabel ?? false
  );
  const handleShowLabel = () => setshowLabel(true);
  const { control } = useFormContext();

  const handleInfoClick: MouseEventHandler<HTMLSpanElement> = e => {
    e.stopPropagation();
    infoClick?.();
  };

  const suffixIcon = info ? (
    <InfoCircleOutlined onClick={handleInfoClick} className={styles.infoIcon} />
  ) : (
    suffix
  );

  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => {
    handleShowLabel();
    onFocus && onFocus(e);
  };

  const handleOnChange =
    (field: ControllerRenderProps<FieldValues, string>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const newValue = value === '' && isNullable ? null : value;
      field.onChange(newValue);
      onChange?.(e);
    };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { isTouched, error } }) => (
        <Row
          className={clsx({
            [styles.inputFieldContainer]: true,
            [styles.error]: !disabled && !!error?.message,
          })}>
          {label && (
            <Col span={24}>
              <FieldLabel
                showLabel={
                  disabled ||
                  showLabel ||
                  isTouched ||
                  !!field.value ||
                  !!error?.message ||
                  field.value === 0
                }
                label={label}
                name={name}
                error={!disabled && !!error?.message}
              />
            </Col>
          )}
          {textarea ? (
            <TextArea
              {...field}
              id={name}
              placeholder={placeholderstring}
              disabled={disabled}
              readOnly={readOnly}
              onFocus={handleFocus}
            />
          ) : (
            <Input
              {...field}
              id={name}
              placeholder={placeholderstring}
              onFocus={handleFocus}
              onChange={handleOnChange(field)}
              onClick={onClick}
              disabled={disabled}
              suffix={suffixIcon}
              readOnly={readOnly}
              {...remainingProps}
            />
          )}

          {!disabled && !!error?.message && <Error message={error?.message} />}
        </Row>
      )}
    />
  );
};

export default RHFInputField;
